"use client"

import { useState } from "react"
import { AlertTriangle, Loader2 } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog"
import { usePermissions } from "@/hooks/use-permissions"
import { useAuth } from "@/hooks/use-auth"
import { TENANTS_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { useAiDisclosureSettingsQuery } from "@/hooks/queries/ai-disclosure-settings"
import {
  useChangeAiDisclosureSettingMutation,
} from "@/hooks/mutations/ai-disclosure-settings"
import {
  AI_DISCLOSURE_CHANNELS,
  AI_DISCLOSURE_OPTOUT_CONFIRMATION,
  type AiDisclosureTemplateChannel,
} from "@/lib/ai-disclosure/settings"
import { AiDisclosureWordingOverrides } from "@/components/organization/ai-disclosure-wording-overrides"

type DisclosureSurface = "organization" | "voice" | "messaging" | "email"

const CHANNELS_BY_SURFACE: Record<DisclosureSurface, readonly AiDisclosureTemplateChannel[]> = {
  organization: AI_DISCLOSURE_CHANNELS,
  voice: [],
  messaging: ["messenger"],
  email: ["email_signature"],
}

export function AiDisclosureSettings({ surface }: { surface: DisclosureSurface }) {
  const { can } = usePermissions()
  const { isLoading: authLoading } = useAuth()
  const { data, isLoading, error } = useAiDisclosureSettingsQuery()
  const changeSetting = useChangeAiDisclosureSettingMutation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const canView = can([TENANTS_PERMISSIONS.VIEW_OWN, TENANTS_PERMISSIONS.EDIT_OWN])
  const canEdit = can(TENANTS_PERMISSIONS.EDIT_OWN)

  if (!canView) return null
  if (authLoading || isLoading) return <p className="text-sm text-muted-foreground">Loading AI disclosure…</p>
  if (!data) return <p className="text-sm text-destructive">{error?.message ?? "AI disclosure settings unavailable."}</p>

  const setEnabled = async (enabled: boolean) => {
    if (!canEdit) return
    if (!enabled) {
      setConfirmOpen(true)
      return
    }
    await changeSetting.mutateAsync({ enabled, expectedEnabled: data.enabled }).catch(() => {})
  }

  const disable = async () => {
    if (!confirmed) return
    await changeSetting.mutateAsync({
      enabled: false,
      expectedEnabled: data.enabled,
      acceptedConfirmationText: AI_DISCLOSURE_OPTOUT_CONFIRMATION,
    })
    setConfirmOpen(false)
    setConfirmed(false)
  }

  return (
    <div className="space-y-6">
    <Card>
      <CardHeader>
        <CardTitle>Customer AI disclosure</CardTitle>
        <CardDescription>
          Help customers understand when they are interacting with AI. EU AI Act transparency requirements may apply.
          This setting and wording affect every agent in your organization.
          {surface === "voice" || surface === "organization"
            ? " Calls are always recorded; the recording notice is added to every voice opening independently of this switch."
            : null}
          <span className="mt-2 block text-xs">
            <a href="https://docs.versafy.ai/workspace/profile-and-organization#prepare-customer-ai-disclosure" target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline underline-offset-2 hover:text-primary/80">
              Read more in docs
            </a>
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-4 rounded-lg border bg-muted/30 px-4 py-3">
          <div className="min-w-0 space-y-1">
            <Label htmlFor={`announce-ai-${surface}`} className="font-medium">Announce AI assistance</Label>
            <p className="text-xs text-muted-foreground">One setting for all agents in your organization.</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span aria-hidden="true" className="text-xs font-medium text-muted-foreground">{data.enabled ? "On" : "Off"}</span>
            <Switch
              id={`announce-ai-${surface}`}
              checked={data.enabled}
              disabled={!canEdit || changeSetting.isPending}
              onCheckedChange={setEnabled}
            />
          </div>
        </div>
        {!data.enabled && (
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300" role="status">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0 space-y-1">
              <p className="font-medium">AI announcements are off for all agents.</p>
              {data.latestChange?.source === "initial_state" && (
                <p className="text-xs">Off since account creation.</p>
              )}
            </div>
          </div>
        )}
        {changeSetting.error && (
          <div role="alert" className="flex items-start gap-2.5 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p className="min-w-0 break-words">{changeSetting.error.message}</p>
          </div>
        )}
        {surface === "voice" && (
          <p className="text-sm text-muted-foreground">
            When enabled, each voice agent’s First Message must say that the caller is speaking with AI.
            Edit the First Message on this agent to choose the wording.
          </p>
        )}
        {CHANNELS_BY_SURFACE[surface].length > 0 && (
          <AiDisclosureWordingOverrides
            channels={CHANNELS_BY_SURFACE[surface]}
            templates={data.templates}
            storeName={data.storeName}
            disabled={!canEdit}
          />
        )}
      </CardContent>
      <Dialog open={confirmOpen} onOpenChange={(open) => { setConfirmOpen(open); if (!open) setConfirmed(false) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Turn off AI assistance disclosure?</DialogTitle>
            <DialogDescription>This change affects all agents in the organization.</DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3 py-2">
            <Checkbox id="confirm-ai-optout" className="mt-1 shrink-0" checked={confirmed} onCheckedChange={value => setConfirmed(value === true)} />
            <Label htmlFor="confirm-ai-optout" className="text-sm leading-relaxed">{AI_DISCLOSURE_OPTOUT_CONFIRMATION}</Label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={!confirmed || changeSetting.isPending} onClick={() => { void disable().catch(() => {}) }}>
              {changeSetting.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Turn off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
    </div>
  )
}
